import _ from 'lodash'
import React from 'react'
import Steps from 'antd/es/steps'
import 'antd/es/steps/style'
import classNames from 'classnames'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { EntityErrorBlock } from 'browser/components/atomic-elements/atoms/error-block/entity-error-block'
import { Footer } from 'browser/components/atomic-elements/atoms/footer/footer'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { buildErrorTree } from 'shared-libs/components/entity/helper'

import 'browser/components/atomic-elements/atoms/form-wizard/_form-wizard.scss'

interface IFormWizardItem {
  /** Formula string; when it evaluates falsy the form is removed from the flow entirely. */
  test?: string
  /** Form label shown in the progress header (e.g. "Entities"). */
  title: string
  /** Marks the final review form (kept for readability; the last visible form is always the review). */
  isReview?: boolean
  /** The form's content, rendered only when this is the active step (e.g. a ui:view of fields). */
  view: any
}

/**
 * A multi-step form wizard. Breaks a long entity form into a sequence of forms shown one at a time,
 * with a progress header and Cancel / Back / Next navigation. The final form's primary button is
 * "Save".
 *
 * Each form has an optional `test` formula (same semantics as ui:conditional): a form whose `test`
 * is falsy is dropped from the flow — it never appears in the header and Next skips over it. `test`
 * is re-evaluated on every render, so a choice on an earlier form can add or remove a later one.
 *
 * @uiComponent
 */
interface IFormWizardProps extends IBaseProps {
  frames: any
  /**
   * The form list. `items` is left raw by the abstract-renderer (see materializeUiSchemaProps —
   * `items` is a recognised raw keyword), so each form's `view` is rendered lazily one at a time
   * rather than all materialized up front.
   */
  items: IFormWizardItem[]
  /**
   * Optional fixed body height. When set, the form body keeps this exact height across all forms
   * (taller forms scroll internally), so the panel and its footer buttons don't jump around as you
   * move between forms of differing content length. A number is px; a string is used verbatim, so
   * pass e.g. "40vh" for a viewport-relative, responsive size.
   */
  fixedHeight?: number | string
  entity?: any
  errors?: any
  onCancel?: () => void
  onSave?: () => Promise<any> | void
}

interface IFormWizardState {
  currentIndex: number
  isSaving: boolean
  stepError: string | null
  // The current form's validation errors ({ valuePath: messages }), shown as a list in the footer
  // alongside the generic message. Empty when the form is clean.
  stepErrors: Record<string, any>
  // Whether the progress header is clickable for free form-to-form jumps. Unlocked once the user
  // has reached the final form (so every form has passed its forward validation); from then on they
  // can jump straight to any form instead of clicking Back repeatedly.
  allowStepNavigation: boolean
}

export class FormWizard extends React.Component<IFormWizardProps, IFormWizardState> {
  private isUnmounted = false

  public state: IFormWizardState = {
    currentIndex: 0,
    isSaving: false,
    stepError: null,
    stepErrors: {},
    allowStepNavigation: false,
  }

  public componentWillUnmount() {
    this.isUnmounted = true
  }

  public render() {
    const { className, fixedHeight } = this.props
    const visible = this.getVisibleItems()
    if (_.isEmpty(visible)) {
      return null
    }
    const currentIndex = this.getCurrentIndex(visible)
    const active = visible[currentIndex]
    const isLast = currentIndex === visible.length - 1

    return (
      <div className={classNames('c-formWizard', className)}>
        <div className='c-formWizard-header'>
          <Steps
            className='vector-progress-steps'
            size='small'
            current={currentIndex}
            // Clickable only after the last form has been reached (everything validated), so jumping
            // around can't skip a form's forward validation.
            onChange={this.state.allowStepNavigation ? this.handleStepClick : undefined}
            items={visible.map((entry, idx) => ({
              title: entry.item.title,
              key: entry.item.title || idx,
            }))}
          />
        </div>
        <div
          className='c-formWizard-body'
          style={fixedHeight ? { height: fixedHeight } : undefined}
        >
          {this.renderActiveForm(active, currentIndex, visible.length)}
        </div>
        {this.renderFooter(currentIndex, isLast)}
      </div>
    )
  }

  private renderActiveForm(
    active: { item: IFormWizardItem; originalIndex: number },
    currentIndex: number,
    total: number,
  ) {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    if (!renderer || !active.item.view) {
      return null
    }
    const uiSchema = active.item.view
    const uiSchemaPath = frames
      .getContext('uiSchemaPath')
      .concat(['items', active.originalIndex, 'view'])
    // Push form-scoped navigation helpers onto the frame stack, the way the storyboard execution
    // renderer exposes goNext/goBack — so a form's own content can drive navigation, and its fields
    // resolve under the correct schema path.
    const stepState = [{
      stepIndex: currentIndex,
      isFirstStep: currentIndex === 0,
      isLastStep: currentIndex === total - 1,
      actionHooks: {
        goNext: this.handleNext,
        goBack: this.handleBack,
        cancel: this.props.onCancel,
        save: this.handleSave,
      },
    }]
    const newFrames = renderer.createChildFrame(frames, { uiSchema, uiSchemaPath }, stepState)
    return renderer.createElementFromFrame(newFrames)
  }

  private renderFooter(currentIndex: number, isLast: boolean) {
    const { onCancel, entity, errors } = this.props
    const { isSaving, stepError, stepErrors } = this.state
    // Prefer this form's errors; on the final form fall back to entity-level errors from Save.
    const displayErrors = !_.isEmpty(stepErrors)
      ? stepErrors
      : (isLast ? errors : undefined)
    // Show the generic lead-in plus the specific field errors (labels resolved by EntityErrorBlock,
    // same as ui:entityFooter elsewhere).
    const errorBlockChildren = !_.isEmpty(displayErrors)
      ? (
        <div className='c-formWizard-footerErrors'>
          {stepError && <div className='c-formWizard-footerErrors-lead'>{stepError}</div>}
          <EntityErrorBlock entity={entity} errors={displayErrors} />
        </div>
      )
      : undefined

    return (
      <Footer
        className='c-formWizard-footer'
        shouldAnimate={false}
        onCancelButtonClick={onCancel}
        cancelButtonText='Cancel'
        isCancelButtonDisabled={isSaving}
        primaryButtonText={isLast ? 'Save' : 'Next'}
        onPrimaryButtonClick={isLast ? this.handleSave : this.handleNext}
        isPrimaryButtonLoading={isSaving}
        primaryButtonGroupElement={this.renderBackButton(currentIndex)}
        errorText={stepError || undefined}
        errorBlockChildren={errorBlockChildren}
      />
    )
  }

  private renderBackButton(currentIndex: number) {
    if (currentIndex === 0) {
      return undefined
    }
    return (
      <div className='c-footer-item'>
        <Button
          data-debug-id='formWizardBackButton'
          isDisabled={this.state.isSaving}
          onClick={this.handleBack}
        >
          Back
        </Button>
      </div>
    )
  }

  private handleBack = () => {
    this.clearFieldHighlights()
    this.setState((prev) => ({
      currentIndex: Math.max(prev.currentIndex - 1, 0),
      stepError: null,
      stepErrors: {},
    }))
  }

  private handleNext = () => {
    const visible = this.getVisibleItems()
    const currentIndex = this.getCurrentIndex(visible)
    const active = visible[currentIndex]
    const paths = this.collectValuePaths(active.item.view)

    void Promise.resolve(this.props.entity?.validate()).then((validationErrors) => {
      if (this.isUnmounted) {
        return
      }
      const stepErrors = this.stepErrorsFor(paths, validationErrors)
      if (!_.isEmpty(stepErrors)) {
        // Highlight the offending fields the same way the entity form does elsewhere: seed the
        // renderer's errorsTree with just this form's errors.
        this.setFieldHighlights(stepErrors)
        this.setState({
          stepError: 'Complete the required fields on this step before continuing.',
          stepErrors,
        })
      } else {
        this.clearFieldHighlights()
        this.setState((prev) => {
          const next = Math.min(prev.currentIndex + 1, visible.length - 1)
          return {
            currentIndex: next,
            stepError: null,
            stepErrors: {},
            allowStepNavigation: prev.allowStepNavigation || next === visible.length - 1,
          }
        })
      }
    })
  }

  // Drive field-level error highlighting through the shared entity renderer (the same instance
  // rendering this form): set its errorsTree so each invalid field shows its red state + message,
  // exactly as on a normal entity create/edit. Editing a field clears its own entry via the
  // renderer's usual onChange path.
  private setFieldHighlights(stepErrors: Record<string, any>) {
    const renderer = this.props.frames.getContext('renderer')
    renderer?.setStateAtPath?.('errorsTree', buildErrorTree(stepErrors))
  }

  private clearFieldHighlights() {
    const renderer = this.props.frames.getContext('renderer')
    renderer?.setStateAtPath?.('errorsTree', {})
  }

  // Jump straight to a form from the (clickable) progress header. Enabled only once the final form
  // has been reached, so this can't bypass forward per-form validation.
  private handleStepClick = (index: number) => {
    this.clearFieldHighlights()
    this.setState({ currentIndex: index, stepError: null, stepErrors: {} })
  }

  private handleSave = () => {
    const { onSave } = this.props
    if (!onSave) {
      return
    }
    this.setState({ isSaving: true, stepError: null })
    return Promise.resolve(onSave())
      .catch(_.noop)
      .then(() => {
        // On success the panel usually closes (unmounts); guard the setState.
        if (!this.isUnmounted) {
          this.setState({ isSaving: false })
        }
      })
  }

  private getVisibleItems(): Array<{ item: IFormWizardItem; originalIndex: number }> {
    const { items = [] } = this.props
    return items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => this.isVisible(item))
  }

  private isVisible(item: IFormWizardItem): boolean {
    if (item.test == null || item.test === '') {
      return true
    }
    try {
      return !!evaluateExpressionWithScopes(this.props.frames, String(item.test), { ...CustomFormulas })
    } catch (e) {
      // A malformed test shouldn't hide a form and strand the user.
      return true
    }
  }

  private getCurrentIndex(visible = this.getVisibleItems()): number {
    return _.clamp(this.state.currentIndex, 0, Math.max(visible.length - 1, 0))
  }

  // Deep-walks a form's view collecting the entity field paths it binds, so we can tell which
  // validation errors belong to this form. Views commonly reference shared sections by `$ref` (e.g.
  // "#/uiSchema/web/entityDetailSection/children/0"), which is only expanded at render time — so we
  // resolve those refs here too, otherwise a $ref-only form looks like it has no fields and the form
  // never gates.
  //
  // A field's real path is its `value` concatenated onto the accumulated scope, the same way the
  // entity renderer builds `valuePath` (see renderer.makeValuePath): '.' inherits the parent scope,
  // '~' resets to root, otherwise the fragment is appended. So we thread `scope` through the walk
  // instead of grabbing raw `value` strings — which would collect relative bindings (`.`, `mixins`,
  // `ingestionFlags.*`) as if absolute. Three guards keep out non-field `value`s the renderer also
  // ignores:
  //  - expression nodes (`{ type: 'expression', value }`) are display/formula, not bindings;
  //  - the prop-mapping bags (mapStateToProps/mapActionsToProps/mapContextToProps) are materialized
  //    separately, not as field bindings;
  //  - dynamic containers (ui:dictionary/ui:list/ui:map) key each entry by a runtime key/index we
  //    can't know statically, so we record the container's own path and stop — its entries' errors
  //    match that path by prefix in stepErrorsFor.
  private static PROP_BAG_KEYS = new Set(['mapStateToProps', 'mapActionsToProps', 'mapContextToProps'])
  private static DYNAMIC_CONTAINER_TYPES = new Set(['ui:dictionary', 'ui:list', 'ui:map'])

  private collectValuePaths(
    node: any,
    scope: string[] = [],
    acc: Set<string> = new Set(),
    seen: Set<string> = new Set(),
  ): Set<string> {
    if (_.isArray(node)) {
      node.forEach((item) => this.collectValuePaths(item, scope, acc, seen))
      return acc
    }
    if (!_.isPlainObject(node)) {
      return acc
    }

    // An expression node is a computed value, not a field binding — skip it and its subtree.
    if (node.type === 'expression') {
      return acc
    }

    let childScope = scope
    if (typeof node.value === 'string') {
      childScope = this.appendValuePath(scope, node.value)
      if (childScope.length > 0) {
        acc.add(childScope.join('.'))
      }
    }

    if (typeof node.$ref === 'string' && !seen.has(node.$ref)) {
      seen.add(node.$ref)
      const resolved = this.resolveSchemaRef(node.$ref)
      if (resolved) {
        this.collectValuePaths(resolved, childScope, acc, seen)
      }
    }

    // The container's own path is recorded above; its item template is dynamically scoped, so don't
    // enumerate the (relative) fields inside it — they're covered by the prefix match.
    if (FormWizard.DYNAMIC_CONTAINER_TYPES.has(node.type)) {
      return acc
    }

    _.forEach(node, (value, key) => {
      if (key !== 'value' && key !== '$ref' && !FormWizard.PROP_BAG_KEYS.has(key) && _.isObject(value)) {
        this.collectValuePaths(value, childScope, acc, seen)
      }
    })
    return acc
  }

  // Append a uiSchema `value` fragment to the running scope, mirroring renderer.makeValuePath: '.'
  // inherits the parent scope, a leading '~' resets to root, otherwise the dot-path is appended.
  private appendValuePath(scope: string[], fragment: string): string[] {
    if (fragment === '.') {
      return scope
    }
    if (fragment[0] === '~') {
      return fragment.slice(1).split('.').filter(Boolean)
    }
    return scope.concat(fragment.split('.'))
  }

  // Resolves a local uiSchema `$ref` (e.g. "#/uiSchema/web/entityDetailSection/children/0") against
  // the entity's schema content, so we can read the fields it points at.
  private resolveSchemaRef(ref: string): any {
    const entity = this.props.entity
    if (!entity || !ref.startsWith('#/')) {
      return null
    }
    const path = ref.slice(2).split('/')
    const schemas = entity.schemas || []
    for (let i = schemas.length - 1; i >= 0; --i) {
      const node = _.get(schemas[i] && schemas[i].content, path)
      if (node) {
        return node
      }
    }
    return null
  }

  // `errors` is keyed by field value-path; return just the entries that belong to this form (its
  // field paths, exact or nested), so we can highlight only this form's fields.
  private stepErrorsFor(paths: Set<string>, errors: any): Record<string, any> {
    if (_.isEmpty(errors) || paths.size === 0) {
      return {}
    }
    const pathList = Array.from(paths)
    return _.pickBy(errors, (_msgs, key) => {
      if (!key || key === 'undefined') {
        return false
      }
      return pathList.some((p) => key === p || key.startsWith(`${p}.`) || p.startsWith(`${key}.`))
    })
  }
}
